import _ from 'lodash'
import React, { useMemo, useContext } from 'react'
import { useWidth } from './useWidth'
import { useSelector, shallowEqual } from 'react-redux'
import { getBreakPoints } from '../lib/getBreakPoints'
import { Map } from 'immutable'
import { LAYOUT_MODE, toUnits } from '../constants/toUnits'
import { IS_SERVER } from '../constants/ssr'
import { parseURLParameters } from '../lib/common'
import { useIsLandingProject } from './useIsLandingProject'
import { DEVICE_DEFAULT_MAX_SIZE } from '../lib/DEVICE_DEFAULT_MAX_SIZE'
import { STATIC_PAGES } from '../components/Project/ProjectUI/PreviewPages/STATIC_PAGES'

const LayoutCtx = React.createContext('lg')

export function LayoutProvider({ children, isPreview = true }) {
  const PREVIEW_ENV = isPreview
  const layoutMode = useSelector((state) => state.getIn(['layoutMode'], 'lg'), shallowEqual)
  const useResponsiveAI = useSelector((state) => state.getIn(['project', 'Settings', 'useResponsiveAI'], true))
  const IsConfiguredDevice = useSelector((state) =>
    state.getIn(['pages', state.getIn(['page']), 'Props', 'IsConfiguredDevice'], Map())
  )
  const isLandingProject = useIsLandingProject()
  const pageID = useSelector((state) => state.get('page'))
  const currentPage = useSelector((state) => state.get('currentPage'))
  const pcDesktopRoot = useSelector(
    (state) =>
      toUnits(
        state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', 'root', 'width'], '1367'),
        state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', 'root', 'widthUnit'], 'px')
      ),
    shallowEqual
  )
  const pcDesktopRootUnit = useSelector(
    (state) => state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', 'root', 'widthUnit'], 'px'),
    shallowEqual
  )
  const extendedLayouts = useSelector((state) =>
    state.getIn(['project', 'Settings', 'responsiveAIConf', 'extendedLayouts'], false)
  )
  const pageWidth = useSelector((state) => {
    let defVal = state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', 'root', 'width'])
    return state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', state.get('page'), 'width'], defVal)
  })
  const pageWidthUnit = useSelector((state) => {
    let defVal = state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', 'root', 'widthUnit'], 'px')
    if (state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', state.get('page'), 'width'])) {
      return state.getIn(['project', 'Settings', 'theme', 'lg', 'Page', state.get('page'), 'widthUnit'], defVal)
    } else {
      return defVal
    }
  })
  // const formFieldTheme = useSelector(state => state.getIn(['project', 'Settings', 'theme', 'FormField'], Map()))

  const configuredDev = useMemo(
    () => ({
      ...IsConfiguredDevice.toJS(),
      lg: true,
      ...(extendedLayouts ? {} : { sml: false }),
    }),
    [IsConfiguredDevice, extendedLayouts]
  )

  const MEDIA_QUERY = useMemo(() => {
    const { MEDIA_QUERY } = getBreakPoints(pageWidth ? toUnits(pageWidth, pageWidthUnit) : pcDesktopRoot)
    let breakPoints = { ...MEDIA_QUERY }
    if ((currentPage && currentPage === pageID && !/^[a-f\d]{24}$/i.test(pageID)) || STATIC_PAGES.includes(pageID)) {
      return breakPoints
    }
    _.each(LAYOUT_MODE, (mode, index) => {
      if (!configuredDev[mode] && !useResponsiveAI && !['sml'].includes(mode)) {
        breakPoints[mode] = breakPoints[LAYOUT_MODE[index - 1]]
      }
    })
    let rootPageSize = parseInt(pcDesktopRoot)

    let specPageSize = parseInt(pageWidth)
    let pageWidth2, pageUnit

    if (specPageSize && !isNaN(specPageSize)) {
      pageWidth2 = specPageSize
      pageUnit = pageWidthUnit
    } else if (rootPageSize && !isNaN(rootPageSize)) {
      pageWidth2 = rootPageSize
      pageUnit = pcDesktopRootUnit
    }
    if (pageUnit == 'px' && !isNaN(pageWidth2)) {
      _.each(DEVICE_DEFAULT_MAX_SIZE, function (val, key) {
        if (pageWidth2 < val && !['lg', 'sml'].includes(key)) {
          breakPoints[key] = `@media (max-width: ${pageWidth2}px)`
        }
      })
      // if(pageWidth2 > 1366) {
      //     breakPoints['md'] = `@media (max-width: ${pageWidth2 - 20}px)`;
      // }
    }
    return breakPoints
  }, [configuredDev, pcDesktopRoot, pageWidth, pageWidthUnit, currentPage, pageID, extendedLayouts])

  let width = useWidth(MEDIA_QUERY)
  if (!extendedLayouts && width === 'sml') {
    width = 'sm'
  }

  if (
    !(currentPage && currentPage === pageID && !/^[a-f\d]{24}$/i.test(pageID)) &&
    !configuredDev[width] &&
    !STATIC_PAGES.includes(pageID)
  ) {
    for (let i = LAYOUT_MODE.indexOf(width); i >= 0; i--) {
      if (configuredDev[LAYOUT_MODE[i]] || useResponsiveAI) {
        width = LAYOUT_MODE[i]
        break
      }
    }
  }

  // const theme = useMemo(() => createMuiProjectTheme(formFieldTheme.toJS()), [formFieldTheme])
  if (!IS_SERVER) {
    try {
      let params = parseURLParameters()
      if (!isLandingProject && params && params.device && LAYOUT_MODE.indexOf(params.device) > -1) {
        width = params.device
      } else if (window) {
        try {
          let windowWidth = document.body.clientWidth
          let rootPageSize = parseInt(pcDesktopRoot)

          let specPageSize = parseInt(pageWidth)
          let pageWidth2, pageUnit

          if (specPageSize && !isNaN(specPageSize)) {
            pageWidth2 = specPageSize
            pageUnit = pageWidthUnit
          } else if (rootPageSize && !isNaN(rootPageSize)) {
            pageWidth2 = rootPageSize
            pageUnit = pcDesktopRootUnit
          }

          if (pageWidth2 && pageUnit === 'px') {
            if (pageWidth2 <= windowWidth - 10) {
              width = 'lg'
            }
          }
        } catch (err) {}
      }
    } catch (err) {
      console.error(err)
    }
  } else {
    width = layoutMode
  }

  return <LayoutCtx.Provider value={PREVIEW_ENV ? width : layoutMode}>{children}</LayoutCtx.Provider>
}

export const useLayoutMode = () => useContext(LayoutCtx)
export const isMobileDevice = (device, isMobile = false) =>
  (isMobile ? ['xs', 'ss'] : ['sm', 'xs', 'ss']).indexOf(device) !== -1
export const iOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)

export const isMobileChecking = () => {
  try {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  } catch (err) {
    return false
  }
}
